/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/templateEditor.scss';

import React, {FocusEventHandler, MouseEventHandler, useContext, useEffect, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {
    AwesomeIcon,
    ChangeKeyFunction,
    ErrorType,
    getSetting,
    Link,
    Loader,
    PDFTextVariables,
    preventDefault,
    voidFunction
} from '@dvrd/dvr-controls';

import EmailTemplate from '../../../../models/emailTemplateModel';

import {BolmateButton, BolmateInput} from '../../../controls';

import {getCustomVariables} from '../../../../util/pdfUtil';

import {AppContext} from '../../../misc/appContext';

import EmailEditor, {EditorRef} from 'react-email-editor';

import {getMergeTags} from '../../../../util/unlayerUtil';

import {EditorType} from './templateEditorController';

import {Editor} from '@tinymce/tinymce-react';

import {NO_REPLY_FLEX_KEY} from '../../../../util/constants';

import {EmailTip, translateEmailTip, UnlayerDesign} from '../../../../util/interfaces';

import classNames from 'classnames';

import EmailDisabledMessage from '../../../misc/emailDisabledMessage';
import {hasAdminLogin} from '../../../../util/adminUtil.ts';

interface Props {
    onClickPreview: MouseEventHandler;
    onCancel: MouseEventHandler;
    onChange: ChangeKeyFunction<any>;
    onClearTemplate: MouseEventHandler;
    onClickTest: (template: EmailTemplate) => void;
    onSubmit: (template: EmailTemplate) => void;
    onResetError: FocusEventHandler;
    onChangeHTMLEditor: (
        data: {
            design: UnlayerDesign;
            html: string;
        },
        callback?: (template?: EmailTemplate) => void
    ) => void;
    onCheckTip: (tip: EmailTip) => MouseEventHandler;
    loading: boolean;
    template: EmailTemplate | null;
    bccError: ErrorType;
    editorType: EditorType;
    showTips: Array<EmailTip>;
    contentWarnings: Array<EmailTip>;
    checkedTips: Array<EmailTip>;
}

export default function TemplateEditor(props: Props) {
    const {t} = useTranslation();
    const context = useContext(AppContext);
    const {
        loading,
        onClearTemplate,
        template,
        onClickTest,
        onClickPreview,
        onCancel,
        onChange,
        onSubmit,
        bccError,
        onResetError,
        editorType,
        onChangeHTMLEditor,
        onCheckTip,
        showTips,
        checkedTips,
        contentWarnings
    } = props;
    const emailPlaceholder =
        context.accountContext.account?.email ?? t('email.template.editor.replyToPlaceholder', 'Antwoordadres');
    const editorRef = useRef<EditorRef>(null);
    const htmlSet = useRef<boolean>(false);
    const [editorLoading, setEditorLoading] = useState(true);
    const currentJSONTemplate = useRef<UnlayerDesign | null>(null);

    function onEditorLoaded() {
        setEditorLoading(false);
    }

    function onChangeField(fieldName: string) {
        return function (evt: React.ChangeEvent<HTMLInputElement>) {
            onChange(fieldName)(evt.target.value);
        };
    }

    function onChangeLegacy(value: string) {
        onChange('body')(value);
    }

    function onReset(evt: React.MouseEvent) {
        if (editorType === EditorType.LEGACY) return onClearTemplate(evt);
        else if (currentJSONTemplate.current && editorRef.current)
            editorRef.current?.editor?.loadDesign(currentJSONTemplate.current);
    }

    function saveHTMLTemplate(callback: (template?: EmailTemplate) => void) {
        return function () {
            if (editorType === EditorType.HTML)
                editorRef.current?.editor?.exportHtml((data: {design: UnlayerDesign; html: string}) => {
                    onChangeHTMLEditor(data, callback);
                });
            else callback();
        };
    }

    function setDesign() {
        if (htmlSet.current || !editorRef.current) return;
        const design = template?.json;
        const hasDesign = !!design && Object.keys(design).length > 0;
        if (hasDesign) {
            currentJSONTemplate.current = design;
            editorRef.current?.editor?.loadDesign(design);
            htmlSet.current = true;
        }
        editorRef.current?.editor?.setMergeTags(getMergeTags(context.accountContext.account));
    }

    function renderVariablesMenu(editor: any) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type -- TinyMCE getSubmenuItems passes a SuccessCallback that expects `string | NestedMenuItemContents[]`; importing the exact TinyMCE type here would tie us to a private d.ts path. `Function` is intentional.
        return function (callback: Function) {
            const {client, company, invoice} = getCustomVariables();
            const items = [
                {
                    type: 'nestedmenuitem',
                    text: t('email.template.editor.var.company', 'Bedrijf'),
                    getSubmenuItems: renderVariables(company, editor)
                },
                {
                    type: 'nestedmenuitem',
                    text: t('email.template.editor.var.client', 'Klant'),
                    getSubmenuItems: renderVariables(client, editor)
                },
                {
                    type: 'nestedmenuitem',
                    text: t('email.template.editor.var.invoice', 'Factuur'),
                    getSubmenuItems: renderVariables(invoice, editor)
                },
                {
                    type: 'nestedmenuitem',
                    text: t('email.template.editor.var.reviewLinks', 'Review Links'),
                    getSubmenuItems: () => {
                        return renderVariables(
                            {
                                product_title: t('email.template.editor.var.productTitle', 'Product Naam'),
                                product_image: t('email.template.editor.var.productImage', 'Product Afbeelding'),
                                product_image_url: t(
                                    'email.template.editor.var.productImageUrl',
                                    'Product Afbeelding URL'
                                ),
                                product_subtitle: t('email.template.editor.var.productSubtitle', 'Product Subtitle'),
                                product_url: t('email.template.editor.var.productUrl', 'Product Review URL')
                            },
                            editor
                        )().concat([
                            {
                                type: 'menuitem',
                                text: t('email.template.editor.var.productReviewLink', 'Product Review Link'),
                                onAction: () => {
                                    // noinspection HtmlUnknownTarget
                                    editor.insertContent(
                                        `<a href='{product_url}'>${t('email.template.editor.var.leaveReview', 'Laat hier je review achter')}</a>`
                                    );
                                }
                            }
                        ]);
                    }
                }
            ];
            callback(items);
        };
    }

    function renderVariables(variables: Record<string, string>, editor: any) {
        return function () {
            const keys = Object.keys(variables);
            // noinspection JSUnusedGlobalSymbols
            return keys.map((key: keyof PDFTextVariables) => ({
                type: 'menuitem',
                text: variables[key],
                onAction: () => {
                    editor.insertContent(`<span>{${key}}</span>`);
                }
            }));
        };
    }

    function renderField(fieldName: string, label: string, value: string, placeholder: string, fieldId: string) {
        return (
            <>
                <label className='field-label' htmlFor={fieldId}>
                    {label}
                </label>
                <input
                    className='field-input'
                    value={value}
                    onChange={onChangeField(fieldName)}
                    id={fieldId}
                    placeholder={placeholder}
                />
            </>
        );
    }

    function renderBCC() {
        const disabled = !hasAdminLogin();
        return (
            <>
                <label className='field-label' htmlFor='bcc-field-input'>
                    BCC
                </label>
                <BolmateInput
                    onChange={onChange('bcc')}
                    value={template?.bcc ?? ''}
                    id='bcc-field'
                    error={bccError}
                    onFocus={onResetError}
                    className='field-input bcc'
                    inputProps={{
                        type: 'email',
                        multiple: true
                    }}
                    ornaments={{
                        element: (
                            <EmailDisabledMessage transform>
                                {t(
                                    'email.template.editor.bccInfo',
                                    'E-mailadressen toevoegen in BCC? Voor de veiligheid vraag je dit bij onze support aan.'
                                )}
                            </EmailDisabledMessage>
                        )
                    }}
                    disabled={disabled}
                />
            </>
        );
    }

    function renderReplyTo() {
        const {account} = context.accountContext;
        const usesNoReply = account?.getFlex(NO_REPLY_FLEX_KEY) === true;
        if (usesNoReply) return null;
        const value = usesNoReply ? '' : template?.replyTo;
        const placeholder = usesNoReply ? undefined : emailPlaceholder;
        return (
            <>
                <label className='field-label' htmlFor='reply-field'>
                    {t('email.template.editor.replyTo', 'Antwoordadres')}
                </label>
                <BolmateInput
                    onChange={usesNoReply ? voidFunction : onChange('reply_to')}
                    value={value ?? ''}
                    id='reply-field'
                    placeholder={placeholder}
                    disabled={usesNoReply}
                    className='field-input'
                />
            </>
        );
    }

    function renderTips() {
        if (!showTips.length && !contentWarnings.length) return null;
        return (
            <div className='tips-container'>
                <label>{t('email.template.editor.tipsTitle', 'Tips voor een effectieve e-mail:')}</label>
                {showTips.map(renderTip)}
                {!!showTips.length && !!contentWarnings.length && <br />}
                {contentWarnings.map(renderContentWarning)}
            </div>
        );
    }

    function renderTip(tip: EmailTip, index: number) {
        const checked = checkedTips.includes(tip);
        return (
            <div key={index} className={classNames('tip', checked && 'checked')} onClick={onCheckTip(tip)}>
                <AwesomeIcon name='check-circle' prefix='far' className='check-icon' />
                <label className='tip-label'>{translateEmailTip(tip, t)}</label>
            </div>
        );
    }

    function renderContentWarning(warning: EmailTip, index: number) {
        return (
            <div key={index} className='tip content-warning'>
                <AwesomeIcon name='triangle-exclamation' className='check-icon' />
                <label className='tip-label'>
                    <span className='red-text'>{t('email.template.editor.policy', 'Beleidspunten:')}</span>{' '}
                    {translateEmailTip(warning, t)}
                </label>
            </div>
        );
    }

    function renderEditor() {
        if (editorType === EditorType.HTML) return renderHTMLEditor();
        return renderLegacyEditor();
    }

    function renderHTMLEditor() {
        return (
            <div className='html-editor-container'>
                <div style={editorLoading ? {display: 'none'} : undefined}>
                    <EmailEditor
                        ref={editorRef}
                        onLoad={onEditorLoaded}
                        projectId={43580}
                        minHeight={700}
                        options={{
                            features: {
                                smartMergeTags: false
                            }
                        }}
                    />
                </div>
            </div>
        );
    }

    function renderLegacyEditor() {
        // noinspection JSUnusedGlobalSymbols
        return (
            <Editor
                apiKey={getSetting('tinyApiKey')}
                value={template?.body ?? ''}
                init={{
                    language: 'nl',
                    language_url: 'https://app.bolmate.nl/tiny/nl.js',
                    height: 700,
                    width: '100%',
                    menubar: false,
                    plugins: [
                        'advlist autolink lists link image',
                        'charmap print preview anchor help',
                        'searchreplace visualblocks code',
                        'insertdatetime media table paste wordcount'
                    ],
                    toolbar:
                        'undo redo | fontselect | fontsizeselect | bold italic underline | \
                                alignleft aligncenter alignright | \
                                bullist numlist outdent indent | link code | variables',
                    link_list: [
                        {
                            title: t('email.template.editor.productReview', 'Product Review'),
                            value: '{product_review_url}'
                        }
                    ],
                    setup: editor => {
                        editor.ui.registry.addMenuButton('variables', {
                            text: t('email.template.editor.variables', 'Variabelen'),
                            fetch: renderVariablesMenu(editor)
                        });
                    },
                    convert_urls: false,
                    relative_urls: false,
                    remove_script_host: false
                }}
                onEditorChange={onChangeLegacy}
            />
        );
    }

    useEffect(() => {
        if (template?.json && !editorLoading) setDesign();
    }, [template, editorLoading]);

    return (
        <div className='email-template-editor'>
            <Loader active={loading} />
            <div className='toolbar'>
                <label className='name-title'>{t('email.template.editor.templateName', 'Naam template')}</label>
                <div className='name-container'>
                    <BolmateInput onChange={onChange('name')} value={template?.name ?? ''} autoSelect />
                </div>
                <div className='buttons-container'>
                    <BolmateButton onClick={onReset} label={t('email.template.editor.reset', 'Reset')} secondary />
                    <BolmateButton
                        onClick={onCancel}
                        label={t('email.template.editor.cancel', 'Annuleren')}
                        secondary
                    />
                    {editorType === EditorType.LEGACY && (
                        <BolmateButton
                            onClick={onClickPreview}
                            label={t('email.template.editor.preview', 'Preview')}
                            secondary
                        />
                    )}
                    <BolmateButton
                        onClick={saveHTMLTemplate(onClickTest)}
                        label={t('email.template.editor.testSend', 'Test verzenden')}
                        secondary
                    />
                    <BolmateButton
                        onClick={saveHTMLTemplate(onSubmit)}
                        label={t('email.template.editor.saveExit', 'Opslaan & Exit')}
                    />
                </div>
            </div>
            <div className='policy-container'>
                <Link
                    className='link'
                    route='https://partnerplatform.bol.com/nl/hulp-nodig/prestaties/beleidspunten-zelfpromotie/'
                >
                    {t('email.template.editor.policyCheck', 'Check of jouw mail voldoet aan het beleid van Bol')}{' '}
                    <AwesomeIcon name='external-link-alt' />
                </Link>
            </div>
            <form className='mail-settings-container' onSubmit={preventDefault}>
                <div className='template-email-settings'>
                    {renderReplyTo()}
                    {renderBCC()}
                    {renderField(
                        'subject',
                        t('email.template.editor.subject', 'Onderwerp'),
                        template?.subject ?? '',
                        t('email.template.editor.subject', 'Onderwerp'),
                        'subject-field'
                    )}
                </div>
                {renderTips()}
                {editorType === EditorType.LEGACY && (
                    <label className='hint'>
                        {t(
                            'email.template.editor.shiftEnterTip',
                            'Tip: gebruik SHIFT + ENTER om een nieuwe regel toe te voegen zonder extra ruimte.'
                        )}
                    </label>
                )}
                {renderEditor()}
            </form>
        </div>
    );
}
